import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, CheckSquare, ListTodo, Users, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PROJECTS } from "@/constants/testIds/crm";
import HighlightText from "@/components/data-management/HighlightText";
import SavedViewsBar from "@/components/data-management/SavedViewsBar";
import StatusCountCards from "@/components/data-management/StatusCountCards";
import PaginationBar from "@/components/data-management/PaginationBar";
import { normalizeListResponse } from "@/lib/listResponse";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";
const SORT_OPTIONS = [
    { value: "-created_at", label: "Newest first" },
    { value: "created_at", label: "Oldest first" },
    { value: "name", label: "Name A-Z" },
    { value: "-name", label: "Name Z-A" },
    { value: "deadline", label: "Nearest deadline" },
    { value: "status", label: "Status A-Z" },
];

export default function Projects() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [users, setUsers] = useState({});
    const [detail, setDetail] = useState(null);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [clientId, setClientId] = useState("");
    const [deadlineFrom, setDeadlineFrom] = useState("");
    const [deadlineTo, setDeadlineTo] = useState("");
    const [sort, setSort] = useState("-created_at");

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.set("page", page);
            params.set("limit", 12);
            if (q) params.set("q", q);
            if (status) params.set("status", status);
            if (clientId) params.set("client_id", clientId);
            if (deadlineFrom) params.set("deadline_from", deadlineFrom);
            if (deadlineTo) params.set("deadline_to", deadlineTo);
            if (sort) params.set("sort", sort);
            const [ps, us] = await Promise.all([api.get(`/projects?${params.toString()}`), api.get("/users")]);
            const normalized = normalizeListResponse(ps.data);
            setItems(normalized.items);
            setCounts(normalized.counts);
            setTotal(normalized.total);
            const m = {};
            normalizeListResponse(us.data).items.forEach((u) => { m[u.id] = u; });
            setUsers(m);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to load projects");
            setItems([]);
            setUsers({});
            setCounts({});
            setTotal(0);
        }
    }, [clientId, deadlineFrom, deadlineTo, page, q, status, sort]);
    useEffect(() => { load(); }, [load]);

    const statuses = useMemo(() => ["planning", "active", "on_hold", "completed", "cancelled"], []);
    const clients = useMemo(() => {
        const unique = new Map();
        items.forEach((p) => {
            if (p.client_id && p.client_name) unique.set(p.client_id, { id: p.client_id, name: p.client_name });
        });
        return [...unique.values()];
    }, [items]);
    const viewState = { q, status, clientId, deadlineFrom, deadlineTo, sort };
    const applyState = (next) => {
        setQ(next.q || "");
        setStatus(next.status || "");
        setClientId(next.clientId || "");
        setDeadlineFrom(next.deadlineFrom || "");
        setDeadlineTo(next.deadlineTo || "");
        setSort(next.sort || "-created_at");
        setPage(1);
    };
    const statusCards = [
        { label: "Total Projects", value: total, hint: "All matching records" },
        { label: "Planning", value: counts.planning || 0 },
        { label: "Active", value: counts.active || 0 },
        { label: "On Hold", value: counts.on_hold || 0 },
        { label: "Completed", value: counts.completed || 0 },
        { label: "Cancelled", value: counts.cancelled || 0 },
    ];

    return (
        <div className="space-y-6" data-testid={PROJECTS.root}>
            <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Delivery</div>
                <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Projects</h1>
                <p className="text-sm text-muted-foreground mt-2">Track scope, team and progress on every won engagement.</p>
                <Link to="/project-pricing" className="inline-flex mt-3 text-sm text-primary underline">Manage client pricing & timelines →</Link>
            </div>

            <SavedViewsBar
                storageKey="crm.projects.views"
                state={viewState}
                applyState={applyState}
                defaultState={{ q: "", status: "", clientId: "", deadlineFrom: "", deadlineTo: "", sort: "-created_at" }}
            />

            <StatusCountCards
                cards={statusCards}
                className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"
            />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search project name or notes…" className="md:col-span-2 h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All statuses</option>
                    {statuses.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
                <select value={clientId} onChange={(e) => { setPage(1); setClientId(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All clients</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input type="date" value={deadlineFrom} onChange={(e) => { setPage(1); setDeadlineFrom(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <input type="date" value={deadlineTo} onChange={(e) => { setPage(1); setDeadlineTo(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.length === 0 && <div className="text-sm text-muted-foreground col-span-full text-center py-12">No projects yet. Convert a won lead to create one.</div>}
                {items.map((p, i) => {
                    const doneTasks = (p.tasks || []).filter((t) => t.status === "done").length;
                    const totalTasks = (p.tasks || []).length;
                    const progress = totalTasks === 0 ? 0 : Math.round((doneTasks / totalTasks) * 100);
                    return (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}
                            data-testid={PROJECTS.card}
                            onClick={() => setDetail(p)}
                            className={`border border-border rounded-md p-5 cursor-pointer hover:-translate-y-[1px] transition-colors transition-transform ${getStatusToneClasses(p.status, { group: "status", variant: "surface" })}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <div className="font-heading text-lg font-semibold tracking-tight"><HighlightText text={p.name} query={q} /></div>
                                    {p.deadline && (
                                        <div className="text-xs text-muted-foreground font-mono mt-1 inline-flex items-center gap-1">
                                            <CalendarDays className="h-3 w-3" /> {new Date(p.deadline).toLocaleDateString()}
                                        </div>
                                    )}
                                </div>
                                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${getStatusToneClasses(p.status, { group: "status", variant: "badge" })}`}>{getStatusLabel(p.status)}</span>
                            </div>
                            <div className="mt-4">
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                                    <span>Progress</span>
                                    <span className="font-mono">{progress}%</span>
                                </div>
                                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                                    <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                                </div>
                            </div>
                            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1"><ListTodo className="h-3.5 w-3.5" /> {totalTasks} tasks</span>
                                <span className="inline-flex items-center gap-1"><CheckSquare className="h-3.5 w-3.5" /> {(p.milestones || []).length} milestones</span>
                                <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {(p.team_members || []).length}</span>
                            </div>
                            <div className="mt-3 flex -space-x-2">
                                {(p.team_members || []).slice(0, 4).map((tid) => {
                                    const u = users[tid];
                                    return (
                                        <div key={tid} className="h-7 w-7 rounded-full bg-primary/10 text-primary border-2 border-card text-[10px] font-semibold inline-flex items-center justify-center">
                                            {(u?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                                        </div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            <PaginationBar total={total} page={page} limit={12} onPageChange={setPage} />

            {detail && <ProjectDetail project={detail} users={users} onClose={() => setDetail(null)} onSaved={load} />}
        </div>
    );
}

function ProjectDetail({ project, users, onClose, onSaved }) {
    const { isAdmin } = useAuth();
    const [proj, setProj] = useState(project);
    const [newTask, setNewTask] = useState("");
    const [newMs, setNewMs] = useState("");

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const remove = async () => {
        if (!window.confirm(`Delete project "${proj.name}"?`)) return;
        try {
            await api.delete(`/projects/${proj.id}`);
            toast.success("Project deleted");
            onSaved && onSaved();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    const save = async (patch) => {
        try {
            const { data } = await api.patch(`/projects/${proj.id}`, patch);
            setProj(data);
            onSaved && onSaved();
            return data;
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Save failed");
            return null;
        }
    };

    const addTask = async () => {
        if (!newTask) return;
        const tasks = [...(proj.tasks || []), { id: crypto.randomUUID(), title: newTask, status: "todo" }];
        const saved = await save({ tasks });
        if (saved) setNewTask("");
    };
    const toggleTask = async (id) => {
        const tasks = (proj.tasks || []).map((t) => t.id === id ? { ...t, status: t.status === "done" ? "todo" : "done" } : t);
        await save({ tasks });
    };
    const addMilestone = async () => {
        if (!newMs) return;
        const milestones = [...(proj.milestones || []), { id: crypto.randomUUID(), title: newMs, completed: false }];
        const saved = await save({ milestones });
        if (saved) setNewMs("");
    };
    const toggleMilestone = async (id) => {
        const milestones = (proj.milestones || []).map((m) => m.id === id ? { ...m, completed: !m.completed } : m);
        await save({ milestones });
    };
    const setStatus = async (status) => {
        await save({ status });
        toast.success(`Status → ${status}`);
    };

    return (
        <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm" onClick={onClose}>
            <motion.aside
                initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="ml-auto h-full w-full max-w-2xl bg-card border-l border-border overflow-y-auto"
            >
                <div className="p-6 border-b border-border sticky top-0 bg-card z-10">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Project</div>
                            <h3 className="font-heading text-2xl font-bold tracking-tight mt-1">{proj.name}</h3>
                        </div>
                        {isAdmin ? (
                            <button
                                onClick={remove}
                                data-testid="projects-delete-button"
                                className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-destructive/40 text-destructive text-xs font-medium hover:bg-destructive/10 transition-colors"
                            >
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                        ) : (
                            <span title="Delete requires admin role" className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-border text-muted-foreground/60 text-xs cursor-not-allowed">
                                <Lock className="h-3.5 w-3.5" /> Admin only
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {["planning", "active", "on_hold", "completed", "cancelled"].map((s) => (
                            <button key={s} onClick={() => setStatus(s)} className={`h-8 px-3 rounded-full text-xs border transition-colors ${
                                proj.status === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
                            }`}>{s.replace("_", " ")}</button>
                        ))}
                    </div>
                </div>
                <div className="p-6 space-y-6">
                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Tasks</h4>
                        <div className="space-y-2">
                            {(proj.tasks || []).map((t) => (
                                <div key={t.id} className={`flex items-center gap-3 border rounded-md px-3 py-2 ${getStatusToneClasses(t.status, { group: "task", variant: "surface" })}`}>
                                    <button onClick={() => toggleTask(t.id)} className={`h-4 w-4 rounded border flex items-center justify-center ${t.status === "done" ? "bg-success border-success text-white" : "border-border"}`}>
                                        {t.status === "done" && <CheckSquare className="h-3 w-3" />}
                                    </button>
                                    <span className={`flex-1 text-sm ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${getStatusToneClasses(t.status, { group: "task", variant: "badge" })}`}>{getStatusLabel(t.status)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-3">
                            <input value={newTask} onChange={(e) => setNewTask(e.target.value)} placeholder="Add task…" className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                            <button onClick={addTask} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors">Add</button>
                        </div>
                    </section>
                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Milestones</h4>
                        <div className="space-y-2">
                            {(proj.milestones || []).map((m) => (
                                <div key={m.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2">
                                    <button onClick={() => toggleMilestone(m.id)} className={`h-4 w-4 rounded-full border flex items-center justify-center ${m.completed ? "bg-primary border-primary" : "border-border"}`} />
                                    <span className={`flex-1 text-sm ${m.completed ? "line-through text-muted-foreground" : ""}`}>{m.title}</span>
                                    {m.deadline && <span className="text-[11px] font-mono text-muted-foreground">{new Date(m.deadline).toLocaleDateString()}</span>}
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-3">
                            <input value={newMs} onChange={(e) => setNewMs(e.target.value)} placeholder="Add milestone…" className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                            <button onClick={addMilestone} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors">Add</button>
                        </div>
                    </section>
                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Team</h4>
                        <div className="flex flex-wrap gap-2">
                            {(proj.team_members || []).map((tid) => {
                                const u = users[tid];
                                if (!u) return null;
                                return (
                                    <div key={tid} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-secondary/50">
                                        <div className="h-6 w-6 rounded-full bg-primary/10 text-primary text-[10px] font-semibold flex items-center justify-center">
                                            {u.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                                        </div>
                                        <span className="text-xs">{u.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                    {proj.notes && (
                        <section>
                            <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2">Notes</h4>
                            <div className="text-sm text-muted-foreground p-3 border border-border rounded-md bg-secondary/20">{proj.notes}</div>
                        </section>
                    )}
                </div>
            </motion.aside>
        </div>
    );
}
